import React from 'react';
import { useTranslation } from 'react-i18next';
import type { VehicleTypes } from '@hero/schema';
import { VehicleDynamicIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export interface VehicleChipProps {
  vehicle: VehicleTypes;
  className?: string;
}

/**
 * Compact chip showing a single vehicle type — vehicle icon + the
 * translated `m-vehicle:types.<type>` label inside a rounded pill.
 *
 * Used anywhere we render a `VehicleTypes[]` (route cards, agreement
 * drawer route rows, client "order settings" preferred-vehicles list,
 * etc.) so the visual language for "this rate / setting applies to
 * these vehicle classes" stays consistent across the dashboards
 * instead of falling back to comma-joined strings on some surfaces
 * and chips on others.
 */
export function VehicleChip({ vehicle, className }: VehicleChipProps) {
  const { t } = useTranslation(['m-vehicle']);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700',
        className,
      )}>
      <VehicleDynamicIcon vehicle={vehicle} className="size-3 text-gray-500" />
      {t(`types.${vehicle}`, { defaultValue: vehicle })}
    </span>
  );
}
